import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useDropzone } from 'react-dropzone';
import Modal from 'components/shared/Modal';
import BWButton from 'components/shared/BWButton';
import { importComboExcel } from 'services/promotions.service';
import { showToast } from 'utils/helpers';

const ImportLayout = styled.div`
  display: grid;
  gap: 16px;
`;

const NoteBox = styled.div`
  padding: 12px 14px;
  border: 1px solid #b2ddff;
  border-radius: 10px;
  background: #eff8ff;
  color: #344054;
  font-size: 13px;
  line-height: 1.55;

  strong {
    display: block;
    margin-bottom: 4px;
    color: #175cd3;
  }
`;

const Dropzone = styled.div`
  display: grid;
  min-height: 138px;
  place-items: center;
  padding: 22px;
  border: 1.5px dashed ${(props) => (props.$hasError ? '#f04438' : '#98a2b3')};
  border-radius: 12px;
  background: ${(props) => (props.$hasError ? '#fff5f5' : '#f8fafc')};
  color: #475467;
  text-align: center;
  cursor: pointer;

  i {
    display: block;
    margin-bottom: 8px;
    color: #ed3237;
    font-size: 24px;
  }

  b {
    display: block;
    color: #101828;
  }

  span {
    display: block;
    margin-top: 4px;
    font-size: 12px;
  }
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;

  @media (max-width: 700px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const SummaryCard = styled.div`
  padding: 12px;
  border: 1px solid #e4e7ec;
  border-radius: 10px;
  background: #fff;

  span {
    display: block;
    color: #667085;
    font-size: 12px;
    font-weight: 700;
  }

  strong {
    display: block;
    margin-top: 4px;
    color: ${(props) => props.$color || '#101828'};
    font-size: 22px;
  }
`;

const SkippedList = styled.div`
  max-height: 260px;
  overflow: auto;
  border: 1px solid #fecdca;
  border-radius: 10px;

  table {
    width: 100%;
    border-collapse: collapse;
  }

  th,
  td {
    padding: 9px 10px;
    border-bottom: 1px solid #fee4e2;
    color: #344054;
    font-size: 12px;
    text-align: left;
    vertical-align: top;
  }

  th {
    position: sticky;
    top: 0;
    background: #fff5f5;
    color: #b42318;
    font-weight: 700;
  }

  tr:last-child td {
    border-bottom: 0;
  }
`;

const ComboImportModal = ({ open, onClose, onImported }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const { getRootProps, getInputProps, acceptedFiles } = useDropzone({
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
    multiple: false,
    maxFiles: 1,
  });
  const currentFile = acceptedFiles?.[0];

  const skippedRows = useMemo(() => result?.skipped || [], [result]);

  const handleImport = async () => {
    if (!currentFile) {
      setError('Vui lòng chọn file .xlsx.');
      return;
    }
    try {
      setUploading(true);
      setError('');
      const response = await importComboExcel(currentFile);
      setResult(response);
      showToast.success(
        `Đã import ${response?.imported_count || 0} combo, bỏ qua ${response?.skipped_count || 0} combo.`,
      );
      onImported?.(response);
    } catch (importError) {
      const message = importError?.message || 'Import combo Excel thất bại.';
      setError(message);
      showToast.error(message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal
      open={open}
      witdh='920px'
      header='Import combo/Bộ sản phẩm từ Excel'
      lalbelClose='Đóng'
      onClose={onClose}
      footer={
        <BWButton
          type='button'
          content={uploading ? 'Đang import...' : 'Import dữ liệu'}
          icon='fi fi-rr-inbox-in'
          disabled={uploading}
          onClick={handleImport}
        />
      }>
      <ImportLayout>
        <NoteBox>
          <strong>Quy tắc import an toàn</strong>
          Hệ thống không tự tạo hàng hóa. Nếu thiếu hoặc có nhiều hàng hóa cùng khớp với một thành phần, toàn bộ
          combo đó sẽ được bỏ qua và hiển thị rõ lý do. Import lại cùng file sẽ cập nhật combo hiện hữu, không tạo
          trùng.
        </NoteBox>
        <Dropzone {...getRootProps()} $hasError={Boolean(error)}>
          <input {...getInputProps()} />
          <div>
            <i className='fi fi-rr-cloud-upload' />
            <b>{currentFile?.name || 'Kéo thả file Excel hoặc bấm để chọn file'}</b>
            <span>Chỉ nhận định dạng .xlsx, tối đa 10 MB</span>
            {error ? <span style={{ color: '#d92d20' }}>{error}</span> : null}
          </div>
        </Dropzone>
        {result ? (
          <>
            <SummaryGrid>
              <SummaryCard>
                <span>Tổng dòng</span>
                <strong>{result.total_rows || 0}</strong>
              </SummaryCard>
              <SummaryCard $color='#027a48'>
                <span>Import thành công</span>
                <strong>{result.imported_count || 0}</strong>
              </SummaryCard>
              <SummaryCard $color='#175cd3'>
                <span>Tạo mới / Cập nhật</span>
                <strong>{`${result.created_count || 0} / ${result.updated_count || 0}`}</strong>
              </SummaryCard>
              <SummaryCard $color='#b42318'>
                <span>Bỏ qua</span>
                <strong>{result.skipped_count || 0}</strong>
              </SummaryCard>
            </SummaryGrid>
            {skippedRows.length ? (
              <SkippedList>
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: 70 }}>Dòng</th>
                      <th style={{ width: '35%' }}>Combo</th>
                      <th>Lý do bỏ qua</th>
                    </tr>
                  </thead>
                  <tbody>
                    {skippedRows.map((item) => (
                      <tr key={`${item.row_number}-${item.combo_code || item.combo_name}`}>
                        <td>{item.row_number}</td>
                        <td>
                          <b>{item.combo_name}</b>
                          {item.combo_code ? <div>{item.combo_code}</div> : null}
                        </td>
                        <td>{(item.reasons || []).join(' ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </SkippedList>
            ) : null}
          </>
        ) : null}
      </ImportLayout>
    </Modal>
  );
};

export default ComboImportModal;
